import { FormEvent, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { authApi } from '@/api/auth.api';
import { useToast } from '@/hooks/use-toast';
import { PasswordRecoveryCopy, PasswordRecoveryStep } from './password-recovery.types';

const STORAGE_KEY = 'yodi-password-recovery';
const SPECIAL_CHARACTER_PATTERN = /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?`~]/;
const STRONG_PASSWORD_PATTERN = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?`~]).{8,}$/;

type PersistedRecovery = {
  identifier: string;
  step: PasswordRecoveryStep;
  method: 'email' | 'sms' | 'unknown';
  maskedTarget: string | null;
  expiresAt: number | null;
};

function readPersisted(): PersistedRecovery | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as PersistedRecovery) : null;
  } catch {
    return null;
  }
}

function persist(value: PersistedRecovery | null) {
  if (!value) {
    sessionStorage.removeItem(STORAGE_KEY);
    return;
  }
  sessionStorage.setItem(STORAGE_KEY, JSON.stringify(value));
}

export function usePasswordRecovery(t: PasswordRecoveryCopy) {
  const { lang } = useParams<{ lang: string }>();
  const currentLang = lang || 'fr';
  const navigate = useNavigate();
  const { toast } = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const initial = readPersisted();

  const requestedStep = searchParams.get('step') as PasswordRecoveryStep | null;
  const safeInitialStep: PasswordRecoveryStep =
    requestedStep === 'new-password'
      ? initial?.identifier
        ? 'otp'
        : 'request'
      : requestedStep || initial?.step || 'request';
  const [step, setStep] = useState<PasswordRecoveryStep>(safeInitialStep);
  const [identifier, setIdentifier] = useState(initial?.identifier || '');
  const [method, setMethod] = useState<'email' | 'sms' | 'unknown'>(initial?.method || 'unknown');
  const [maskedTarget, setMaskedTarget] = useState<string | null>(initial?.maskedTarget || null);
  const [expiresAt, setExpiresAt] = useState<number | null>(initial?.expiresAt || null);
  const [otp, setOtp] = useState('');
  const [otpToken, setOtpToken] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [now, setNow] = useState(Date.now());

  const passwordHasMinLength = password.length >= 8;
  const passwordHasLetterCase = /[a-z]/.test(password) && /[A-Z]/.test(password);
  const passwordHasDigitAndSpecial = /\d/.test(password) && SPECIAL_CHARACTER_PATTERN.test(password);
  const passwordIsStrong = STRONG_PASSWORD_PATTERN.test(password);
  const passwordsMatch = password.length > 0 && password === confirmPassword;
  const canSubmitPassword = passwordIsStrong && passwordsMatch && Boolean(otpToken) && !loading;
  const canVerifyOtp = /^\d{6}$/.test(otp) && Boolean(identifier.trim()) && !loading;
  const remainingSeconds = expiresAt ? Math.max(0, Math.ceil((expiresAt - now) / 1000)) : null;

  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, []);

  useEffect(() => {
    setSearchParams(step === 'request' ? {} : { step }, { replace: true });
  }, [setSearchParams, step]);

  useEffect(() => {
    if (step === 'request') return;
    if (step === 'success') return;
    persist({ identifier, step, method, maskedTarget, expiresAt });
  }, [expiresAt, identifier, maskedTarget, method, step]);

  const formattedRemaining = useMemo(() => {
    if (remainingSeconds === null) return null;
    const minutes = Math.floor(remainingSeconds / 60);
    const seconds = remainingSeconds % 60;
    return `${minutes}:${String(seconds).padStart(2, '0')}`;
  }, [remainingSeconds]);

  const requestReset = async (event?: FormEvent) => {
    event?.preventDefault();
    const cleanIdentifier = identifier.trim();
    if (cleanIdentifier.length < 3) {
      toast({ title: t.emailInvalid, description: t.emailInvalidDesc, variant: 'destructive' });
      return;
    }

    setLoading(true);
    try {
      const response = await authApi.forgotPassword({ identifier: cleanIdentifier });
      const nextStep: PasswordRecoveryStep = response.method === 'sms' ? 'otp' : 'email-sent';
      setIdentifier(cleanIdentifier);
      setMethod(response.method);
      setMaskedTarget(response.maskedTarget);
      setExpiresAt(response.expiresInSeconds ? Date.now() + response.expiresInSeconds * 1000 : null);
      setStep(nextStep);
      toast({ title: t.emailSentTitle, description: response.message || t.emailSentDesc });
    } catch (error: any) {
      toast({ title: t.error, description: error?.response?.data?.message || t.submitFailed, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const verifyOtp = async (event: FormEvent) => {
    event.preventDefault();
    if (!canVerifyOtp) return;

    setLoading(true);
    try {
      const response = await authApi.verifyPasswordResetOtp({ identifier, otp });
      setOtpToken(response.otpToken);
      setExpiresAt(Date.now() + response.expiresInSeconds * 1000);
      setStep('new-password');
    } catch (error: any) {
      toast({ title: t.error, description: error?.response?.data?.message || t.submitFailed, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const submitPassword = async (event: FormEvent) => {
    event.preventDefault();
    if (!passwordIsStrong) {
      toast({ title: t.resetPasswordTooShort, description: t.resetPasswordTooShortDesc, variant: 'destructive' });
      return;
    }
    if (!passwordsMatch) {
      toast({ title: t.resetPasswordMismatch, description: t.resetPasswordMismatchDesc, variant: 'destructive' });
      return;
    }

    setLoading(true);
    try {
      await authApi.resetPasswordWithOtp({ otpToken, password, confirmPassword });
      persist(null);
      setOtpToken('');
      setPassword('');
      setConfirmPassword('');
      setStep('success');
      window.setTimeout(() => navigate(`/${currentLang}/login`), 1800);
    } catch (error: any) {
      toast({ title: t.error, description: error?.response?.data?.message || t.submitFailed, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  // A username may reach an account without e-mail: its code came by SMS.
  const enterSmsCode = () => {
    setOtp('');
    setExpiresAt(Date.now() + 10 * 60 * 1000);
    setStep('otp');
  };

  const restart = () => {
    persist(null);
    setIdentifier('');
    setOtp('');
    setOtpToken('');
    setMaskedTarget(null);
    setExpiresAt(null);
    setStep('request');
  };

  return {
    currentLang,
    step,
    identifier,
    method,
    maskedTarget,
    otp,
    password,
    confirmPassword,
    showPassword,
    showConfirmPassword,
    loading,
    formattedRemaining,
    canVerifyOtp,
    canSubmitPassword,
    enterSmsCode,
    passwordHasMinLength,
    passwordHasLetterCase,
    passwordHasDigitAndSpecial,
    passwordsMatch,
    setIdentifier,
    setOtp: (value: string) => setOtp(value.replace(/\D/g, '').slice(0, 6)),
    setPassword,
    setConfirmPassword,
    setShowPassword,
    setShowConfirmPassword,
    requestReset,
    verifyOtp,
    submitPassword,
    restart,
    goToLogin: () => navigate(`/${currentLang}/login`),
  };
}

